/**
 * @file page-diff-lines.ts
 * @description «Что изменено» страницы строками для людей (phase19.16 Р3а;
 * SB126; uiux-r3 §3.1–§3.2): «Адрес: /about → /about-us», «Блок «Отзывы» —
 * добавлен», «Изменён порядок блоков: 2». Источник — `diff.items` из shared
 * `diffSitePage` (сервер отдаёт его в `GET pages/:id/changes`).
 *
 * Слова изменений — реестром (OCP): новый вид изменения — запись.
 */

import type { SitePageDiffItem, SitePageSectionChange } from "@chaiberi/shared";

/** Вид строки: оттенок значка в списке. */
export type DiffLineTone = "added" | "changed" | "removed";

export interface DiffLine {
  key: string;
  text: string;
  tone: DiffLineTone;
}

const SECTION_WORD: Readonly<Record<SitePageSectionChange, string>> = {
  added: "добавлен",
  changed: "изменён",
  removed: "удалён",
};

/** Строка одного изменения. */
export function diffLineOf(item: SitePageDiffItem, index: number): DiffLine {
  if (item.type === "field") {
    const before = item.before ?? "—";
    const after = item.after ?? "—";
    const text = item.before === null && item.after !== null ? `${item.label}: ${after}` : `${item.label}: ${before} → ${after}`;
    return { key: `f:${item.field}`, text, tone: "changed" };
  }
  if (item.type === "order") return { key: `o:${index}`, text: `${item.label}: ${item.moved}`, tone: "changed" };
  return { key: `s:${item.key}:${item.change}`, text: `Блок «${item.title}» — ${SECTION_WORD[item.change]}`, tone: item.change };
}

/** Все строки сводки. */
export function diffLines(items: readonly SitePageDiffItem[]): DiffLine[] {
  return items.map(diffLineOf);
}

/** Метки блоков в структуре страницы (uiux-r3 §3.1): «● изменён / ● новый» и число удалённых. */
export interface BlockMarks {
  /** Ключ блока (= id строки блока) → «изменён» / «новый». */
  marks: Record<string, string>;
  removed: number;
}

/** Метки по сводке черновика; первая публикация (версии нет) — без меток: новое всё. */
export function blockMarksOf(diff: { first: boolean; items: readonly SitePageDiffItem[] } | null | undefined): BlockMarks {
  const out: BlockMarks = { marks: {}, removed: 0 };
  if (!diff || diff.first) return out;
  for (const item of diff.items) {
    if (item.type !== "section") continue;
    if (item.change === "removed") out.removed += 1;
    else out.marks[item.key] = item.change === "added" ? "новый" : "изменён";
  }
  return out;
}
